/**
 * What a manual settlement is allowed to repay.
 *
 * Settling REPAYS credit that was drawn, so the ceiling is the credit currently used — not
 * the wallet's maximum credit. The wallet service enforces this as `CreditBalance - amount
 * < 0`, which on a wallet that has drawn nothing refuses EVERY amount. That refusal is
 * correct; what made it a reported bug is that it arrived only after submitting, in a
 * message naming max credit — a limit with nothing to do with the reason.
 *
 * These live outside the modal so the rule can be tested without mounting a component that
 * needs Apollo, role configuration and a theme to render one caption.
 */

interface CreditFacts {
  maxCredit?: number
  /** The service sends `maxCredit - creditBalance`; the drawn amount inverts it. */
  remainingCreditBalance?: number
}

/**
 * Credit actually drawn, and so the most a settlement can repay.
 *
 * Clamped at zero: `remainingCreditBalance` exceeding `maxCredit` is an inconsistent record
 * rather than a negative debt, and it must not present as a settleable amount.
 */
export const outstandingCreditOf = (wallet: CreditFacts): number =>
  Math.max(0, (wallet.maxCredit ?? 0) - (wallet.remainingCreditBalance ?? 0))

/**
 * Whether this submission would be refused by the service.
 *
 * Only meaningful in settlement mode — a plain charge has no such ceiling, and treating one
 * as if it did would block the ordinary path.
 */
export const settlementExceedsCredit = (
  manualSettlement: boolean,
  amount: number,
  outstandingCredit: number
): boolean => manualSettlement && amount > outstandingCredit

/**
 * Whether the operator must pick a payment gateway before submitting.
 *
 * Only the gateway path does — a manual settlement books the transaction directly and never
 * touches a gateway. The distinction matters because the gateway picker itself only renders
 * OUTSIDE settlement mode, so in settlement mode no gateway can be chosen: requiring one
 * there disables the button permanently, whatever else is valid.
 *
 * A bilateral wallet is excluded for the separate reason that it selects a creditor instead,
 * which the submit chain checks on its own.
 */
export const requiresGatewaySelection = (
  isBilateral: boolean,
  manualSettlement: boolean
): boolean => !isBilateral && !manualSettlement
